/**
 * @fileoverview Syndicator Showdown — Game Rules Page
 * @module pages/GameRulesPage
 * Route: /rules
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './GameRulesPage.css';

const Section = ({ number, title, children }) => (
  <section className="gr-section">
    <div className="gr-section-header">
      <span className="gr-section-num">{number}</span>
      <h2 className="gr-section-title">{title}</h2>
    </div>
    <div className="gr-section-body">{children}</div>
  </section>
);

const Rule = ({ children }) => (
  <div className="gr-rule">{children}</div>
);

export const GameRulesPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
  <div className="gr-page">

    <div className="gr-hero">
      <Link to="/" className="gr-back">← Back to Home</Link>
      <h1 className="gr-title">Game Rules</h1>
      <p className="gr-subtitle">
        Syndicator Showdown — Official Rules &amp; Mechanics
      </p>
      <p className="gr-updated">Last updated: June 2026</p>
    </div>

    <div className="gr-content">

      {/* ── 1. How It Works ── */}
      <Section number="1" title="How It Works">
        <Rule>
          Each game is built around a real Australian race meeting. You receive
          a virtual bankroll and use it to purchase fractional ownership shares
          in racehorses across multiple race legs.
        </Rule>
        <Rule>
          Shares are priced per 1% equity using a power-curve model tied to
          each runner's market odds and the race prize money. Shorter-priced
          runners cost more per share.
        </Rule>
        <Rule>
          You compete against other players on a leaderboard. Your score is
          the total virtual prize money your stable earns across all race legs.
        </Rule>
      </Section>

      {/* ── 2. Bankroll & Spend Rules ── */}
      <Section number="2" title="Bankroll &amp; Spend Rules">
        <Rule>
          Your bankroll is fixed at game setup. You cannot add funds once the
          game opens.
        </Rule>
        <Rule>
          You must spend a minimum amount on each race leg. This ensures you
          engage with every leg of the game rather than concentrating all funds
          on one race.
        </Rule>
        <Rule>
          A maximum cap per race prevents any player from spending their entire
          bankroll on a single leg. The cap is calculated as:
          total bankroll − (number of remaining legs × minimum floor).
        </Rule>
        <Rule>
          Unspent bankroll at game close earns nothing. Spend it or lose it.
        </Rule>
      </Section>

      {/* ── 3. Share Pricing & Tranches ── */}
      <Section number="3" title="Share Pricing &amp; Tranches">
        <Rule>
          Base share price is calculated as:{' '}
          <code>K × Prize Money × (1 / Odds)^0.914 ÷ 100</code>.
          The K multiplier is set by the game operator to calibrate price levels.
        </Rule>
        <Rule>
          Shares are sold in five price tranches. As you buy more equity in a
          single runner, the price per share increases:
        </Rule>
        <div className="gr-tranche-table">
          <div className="gr-tranche-row gr-tranche-row--header">
            <span>Tranche</span><span>Equity Range</span><span>Price Multiplier</span>
          </div>
          {[
            ['1', 'First 30%',  '1.00× (base)'],
            ['2', 'Next 25%',   '1.10×'],
            ['3', 'Next 20%',   '1.25×'],
            ['4', 'Next 15%',   '1.50×'],
            ['5', 'Final 10%',  '2.00×'],
          ].map(([t, range, mult]) => (
            <div key={t} className="gr-tranche-row">
              <span>T{t}</span><span>{range}</span><span>{mult}</span>
            </div>
          ))}
        </div>
        <Rule>
          Buying 100% of a runner costs 1.25× the base price across the full
          field — this is intentional. You cannot profitably corner the market
          on any runner.
        </Rule>
      </Section>

      {/* ── 4. Price Lock & Entry Closing ── */}
      <Section number="4" title="Price Lock &amp; Entry Closing">
        <Rule>
          Share prices are locked approximately 30 minutes before the first
          race jumps. No purchases or changes are permitted after price lock.
        </Rule>
        <Rule>
          Depending on the game format, entries may close at first race jump
          (simpler), or per individual race (each leg closes at its own jump
          time, allowing late entries on later legs).
        </Rule>
        <Rule>
          The game close time is always clearly displayed on the game card.
          Late entries are not accepted under any circumstances.
        </Rule>
      </Section>

      {/* ── 5. Buying, Selling & Exchanges ── */}
      <Section number="5" title="Buying, Selling &amp; Exchanges">
        <Rule>
          You can buy or sell equity in any runner, in any race, at any time
          before that race locks. Locking happens either at the first race jump
          of the showdown, or per individual race — check each game's settings.
        </Rule>
        <Rule>
          <strong>Selling your equity:</strong> If you choose to sell some or
          all of your equity in a runner before lock, you'll receive 90% of your
          original purchase cost back to your wallet. A 10% commission applies
          to all voluntary sales.
        </Rule>
        <Rule>
          <strong>Race minimums still apply:</strong> After selling, you must
          ensure each race in your entry still meets the minimum spend
          requirement before you can submit your final entry. You can buy equity
          in other runners to top up any shortfall.
        </Rule>
        <Rule>
          <strong>Scratched runners are different:</strong> If a runner you hold
          is officially scratched, you receive a full 100% refund — no
          commission applies. You'll be notified by email and in your stable
          with options to reallocate those funds.
        </Rule>
        <Rule>
          <strong>After lock:</strong> Once a race locks, no buying, selling, or
          transferring is possible for that race — except in the case of an
          official scratching, which still allows reallocation as described
          above.
        </Rule>
      </Section>

      {/* ── 6. Scratchings ── */}
      <Section number="6" title="Scratchings">
        <Rule>
          If a runner is scratched before race jump, your equity purchase in
          that runner is voided and the cost is refunded to your race budget
          for that leg.
        </Rule>
        <Rule>
          You can manually reallocate your refunded funds to any remaining
          active runner before the race jumps.
        </Rule>
        <Rule>
          If you do not reallocate before jump time, the system automatically
          applies your funds using the following priority:
        </Rule>
        <div className="gr-priority-list">
          <div className="gr-priority-item">
            <span className="gr-priority-num">1</span>
            <span>
              <strong>Proportional top-up</strong> — if you hold equity in
              other runners in the same race, your refund is distributed across
              those runners in proportion to your existing holdings.
            </span>
          </div>
          <div className="gr-priority-item">
            <span className="gr-priority-num">2</span>
            <span>
              <strong>Race favourite</strong> — if you have no other holdings
              in the race, your refund is allocated to the runner with the
              lowest market odds (the favourite).
            </span>
          </div>
          <div className="gr-priority-item">
            <span className="gr-priority-num">3</span>
            <span>
              <strong>Lowest barrier</strong> — if odds are not available,
              allocation goes to the runner in barrier 1.
            </span>
          </div>
        </div>
        <Rule>
          Barrier scratchings (at the barriers, on race day) are treated
          identically to pre-race scratchings — your equity is voided and
          refunded.
        </Rule>
      </Section>

      {/* ── 7. Steward Non-Runner Rulings ── */}
      <Section number="7" title="Steward Non-Runner Rulings">
        <Rule>
          In rare cases, racing stewards may declare a runner a non-runner
          after the race has been run (e.g. due to a barrier incident or
          post-race veterinary finding).
        </Rule>
        <Rule>
          In this event, the race result for all other runners stands unchanged.
          The non-runner's equity is voided and your purchase cost is refunded.
          No reallocation occurs — the race is over.
        </Rule>
        <Rule>
          Non-runner rulings are applied by the game administrator following
          the official stewards' decision. They are final and cannot be appealed
          within the game.
        </Rule>
      </Section>

      {/* ── 8. Prize Dividends ── */}
      <Section number="8" title="Prize Dividends">
        <Rule>
          When a race is settled, each placed runner earns a virtual prize
          dividend based on their finishing position.
        </Rule>
        <Rule>
          Your dividend from a runner = your equity % × that runner's prize
          for their finishing position.
        </Rule>
        <Rule>
          Dead heats split the combined prize of the tied positions equally
          between the dead-heaters. For example, a 2-way dead heat for 1st
          gives each runner half of (1st prize + 2nd prize).
        </Rule>
        <Rule>
          Your total score is the sum of all dividends earned across every
          race leg in the game.
        </Rule>
      </Section>

      {/* ── 9. Results & Disputes ── */}
      <Section number="9" title="Results &amp; Disputes">
        <Rule>
          Race results are entered by the game administrator based on the
          official result declared by the Principal Racing Authority (PRA)
          for that jurisdiction.
        </Rule>
        <Rule>
          Results are final once settled by the administrator. In the event
          of an error in data entry, the administrator may correct the result
          before final leaderboard standings are published.
        </Rule>
        <Rule>
          Syndicator Showdown does not accept result disputes based on
          personal opinion of race outcomes. Only official PRA decisions
          affect game results.
        </Rule>
      </Section>

      {/* ── 10. Responsible Play ── */}
      <Section number="10" title="Responsible Play">
        <Rule>
          Syndicator Showdown uses virtual currency only. No real money is
          wagered unless an entry fee is explicitly stated on the game card.
        </Rule>
        <Rule>
          You must be 18 years or older to participate. By entering a game
          you confirm you meet the age requirement in your jurisdiction.
        </Rule>
        <Rule>
          If you or someone you know needs support with gambling, contact
          Gambling Help Online on <strong>1800 858 858</strong> or visit{' '}
          <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer">
            gamblinghelponline.org.au
          </a>.
        </Rule>
      </Section>

    </div>

    <div className="gr-footer">
      <Link to="/" className="gr-back-btn">← Back to Home</Link>
      <Link to="/signin?mode=signup" className="gr-play-btn">Play Now →</Link>
    </div>

  </div>
  );
};

export default GameRulesPage;
